import { Archive, Inbox } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/marketing/components/ui/button";
import { InfluencerCampaignStatus } from "@/marketing/models/influencerCampaign";
import { influencerCampaignAPIClient } from "@/marketing/infrastructure/influencer/campaign/influencerCampaignAPIClient";
import { INFLUENCER_CAMPAIGN_KEYS } from "@/marketing/infrastructure/influencer/campaign/utils/keys";

interface RightRailProps {
  campaignId: string;
  campaignName: string;
}

export function RightRail({ campaignId, campaignName }: RightRailProps) {
  const queryClient = useQueryClient();

  const archiveMutation = useMutation({
    mutationFn: () =>
      influencerCampaignAPIClient.updateInfluencerCampaignStatus(campaignId, {
        status: InfluencerCampaignStatus.Completed,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: INFLUENCER_CAMPAIGN_KEYS.detail(campaignId),
      });
      void queryClient.invalidateQueries({
        queryKey: INFLUENCER_CAMPAIGN_KEYS.lists(),
      });
      toast.success(`Archived ${campaignName}`);
    },
    onError: () => toast.error("Failed to archive campaign"),
  });

  const handleArchive = () => {
    if (
      !window.confirm(
        `Archive "${campaignName}"? Status will be set to Completed.`,
      )
    )
      return;
    archiveMutation.mutate();
  };

  return (
    <aside className="tw-hidden tw-w-[280px] tw-flex-shrink-0 tw-flex-col tw-border-l tw-border-border tw-bg-background lg:tw-flex">
      <div className="tw-flex tw-flex-col tw-gap-3 tw-border-b tw-border-border tw-px-4 tw-py-4">
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
          <Inbox className="tw-h-3 tw-w-3" />
          Inbox · this campaign
        </div>
        <div className="tw-rounded-md tw-border tw-border-dashed tw-border-border tw-px-3 tw-py-6 tw-text-center tw-text-[12px] tw-text-muted-foreground">
          Inbox + AI Assist arrive in v1.1.
          <br />
          Until then, comments live on each influencer's slide-over.
        </div>
      </div>

      <div className="tw-mt-auto tw-border-t tw-border-border tw-px-4 tw-py-3">
        <Button
          variant="outline"
          size="sm"
          className="tw-w-full tw-justify-center tw-gap-1.5"
          onClick={handleArchive}
          disabled={archiveMutation.isPending}
        >
          <Archive className="tw-h-3.5 tw-w-3.5" />
          {archiveMutation.isPending ? "Archiving…" : "Archive campaign"}
        </Button>
      </div>
    </aside>
  );
}
